import * as React from 'react';
import { useFormContext } from 'react-hook-form';
import { useAtomValue } from 'jotai';
import { useWizard } from 'react-use-wizard';
import { useTranslation } from 'next-i18next';

import { Heading, Spinner } from '@restworld/ui-ds-base';
import { OptionsType, useOccupationsByDomainInterest } from '@restworld/utils-common';

import { domainInterests as domainInterestsStatic } from '../../../job-explorer-filters';
import { ChipCheckboxFormField } from '../../../job-explorer-more-filter/job-explorere-more-filter.components';
import { StepWrapper, SaveResearchFormFields, areaSelectionAtom } from '../job-explorer-save-research-v2';
import { SeasonalFilter } from './address-view-and-selection';
import { domainInterestAtom } from '../../../job-explorer-server.atoms';

export const FilterSelection = () => {
  const domainInterest = useAtomValue(domainInterestAtom);
  const { watch } = useFormContext<SaveResearchFormFields>();
  const selectedDomainInterests: string[] = watch('domain_interest');
  const sortedDmOptions: OptionsType[] = React.useMemo(
    () =>
      domainInterestsStatic.map((item) => ({
        ...item,
        id: domainInterest?.find(({ key }) => key === item.key)?.id,
      })),
    [domainInterest]
  );
  const { activeStep } = useWizard();
  const areaSelection = useAtomValue(areaSelectionAtom);
  const { t } = useTranslation('common');
  return (
    <StepWrapper>
      <div data-cy="save-research-filters" className="flex flex-col gap-8">
        <div className="flex flex-col gap-2">
          <Heading size={3} className="leading-[28.8px]">
            {t('tell_us_what_you_are_looking_for')}
          </Heading>
          <Heading size={5} weight="normal" color="muted" className="leading-[24px]">
            {t('enjoy_your_free_time')}
          </Heading>
        </div>
        {activeStep === 2 && areaSelection === 'whole_italy' && <SeasonalFilter />}
        <ChipCheckboxFormField
          customChipGroupStyles={'grid grid-cols-2 gap-2 md:grid-cols-3 lg:grid-cols-2'}
          title={
            <Heading size={5} weight="medium" className="leading-[24px]">
              {t('sector')}
            </Heading>
          }
          options={sortedDmOptions}
          id="domain_interest"
          isSingleSelectMode={false}
          testId="save-research-domain-interest-filter"
        />
        {!selectedDomainInterests?.length && (
          <div className="flex flex-col gap-2 saturate-25 opacity-50 pointer-events-none cursor-not-allowed">
            <Heading size={5} weight="medium" className="leading-[24px]">
              {t('qualification')}
            </Heading>
            <ChipCheckboxFormField
              customChipGroupStyles={'grid grid-cols-2 md:grid-cols-3 gap-2'}
              options={[
                {
                  id: '51',
                  key: '51',
                  label: t('room_attendant'),
                },
                {
                  id: '54',
                  key: '54',
                  label: 'Assistant',
                },
              ]}
              id=""
            />
          </div>
        )}
        {!!selectedDomainInterests?.length && (
          <div className="flex flex-col gap-4">
            <Heading size={5} weight="medium" className="leading-[24px]">
              {t('qualification')}
            </Heading>
            {selectedDomainInterests.map((v) => (
              <OccupationSelect key={v} domainInterestId={v} />
            ))}
          </div>
        )}
      </div>
    </StepWrapper>
  );
};

export default FilterSelection;
export const OccupationSelect = ({
  domainInterestId,
  customGridStyles = 'grid gap-2 md:grid-cols-2',
  formId = 'occupation_id',
}: {
  domainInterestId?: string;
  customGridStyles?: string;
  formId?: string;
}) => {
  const domainInterest = useAtomValue(domainInterestAtom);
  const sortedDmOptions: OptionsType[] = React.useMemo(
    () =>
      domainInterestsStatic.map((item) => ({
        ...item,
        id: domainInterest?.find(({ key }) => key === item.key)?.id,
      })),
    [domainInterest]
  );
  const { occupationOptions, loading } = useOccupationsByDomainInterest({
    domainInterestId,
    domainInterest,
  });
  return loading ? (
    <div className="flex items-center justify-center">
      <Spinner color="primary" size="sm" />
    </div>
  ) : (
    <div className="flex flex-col gap-2">
      <Heading size={6} weight="normal" className="leading-[21px]">
        {sortedDmOptions.find((v) => v.id === domainInterestId)?.label || ''}
      </Heading>
      <ChipCheckboxFormField
        customChipGroupStyles={customGridStyles}
        options={occupationOptions}
        id={`${formId}.${domainInterestId}`}
        testId="save-research-occupation-filter"
      />
    </div>
  );
};
